/**
 * ClientBriefIdeaModal
 *
 * Read-only idea detail shown as a centered modal over the brief page
 * (`ClientBriefRoute`). Ideas reach a brief only after they are approved, so
 * this surface carries no review chrome — no star rating, no comment. It
 * renders the shared `IdeaDetailBody` read-only, the same body the client saw
 * when voting.
 */

import React from "react";
import { Modal } from "@/components/ui/Modal";
import { IdeaDetailBody } from "@/domains/Ideas/components/IdeaDetailBody";
import { useLocale } from "@/hooks/useLocale";
import type { Idea } from "@/domains/Ideas/types";

export const CONTENT_TYPE_IT: Record<string, string> = {
  POST: "Post",
  REEL: "Reel",
  CAROUSEL: "Carousel",
  STORY: "Story",
};

interface ClientBriefIdeaModalProps {
  idea: Idea | null;
  isOpen: boolean;
  onClose: () => void;
}

export const ClientBriefIdeaModal: React.FC<ClientBriefIdeaModalProps> = ({
  idea,
  isOpen,
  onClose,
}) => {
  const { t } = useLocale();

  const header = idea ? (
    <div>
      <p className="text-[10px] font-semibold text-gray-500 uppercase tracking-[0.12em]">
        {idea.contentType
          ? CONTENT_TYPE_IT[idea.contentType] || idea.contentType
          : "Idea"}
      </p>
      <h2 className="mt-1 text-2xl font-bold tracking-tight text-gray-900 leading-tight">
        {idea.title}
      </h2>
    </div>
  ) : undefined;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      header={header}
      bodyPadding
      size="lg"
    >
      {idea && (
        <IdeaDetailBody
          resources={idea.resources || []}
          body={idea.body || {}}
          sourcesLabel={t("idea.sources")}
          sectionLabels={{
            concept: t("idea.section.concept"),
            objective: t("idea.section.objective"),
            caption: t("idea.section.caption"),
            voiceover_script: t("idea.section.voiceover_script"),
            storyboard: t("idea.section.storyboard"),
          }}
        />
      )}
    </Modal>
  );
};
